import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHead } from '../ui';

/** Step 1: e-mail (can be prefilled via ?email=…), step 2: 6-digit code from the mail.
 *  Each step has its own key: otherwise React reuses the buttons between the steps, and the click on
 *  "Andere Adresse" (type=button) turns into a click on "Code senden" (submit) while it is still being handled. */
export default function Login() {
  const [params] = useSearchParams();
  // prefilled from an invitation (#email=…, never sent to the server) or ?email=…
  const [email, setEmail] = useState(
    () => params.get('email') || new URLSearchParams(window.location.hash.slice(1)).get('email') || '',
  );
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const nav = useNavigate();

  async function requestCode(ev?: FormEvent) {
    ev?.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api('/auth/request', { method: 'POST', body: { email } });
      setStep('code');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function verify(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api('/auth/verify', { method: 'POST', body: { email, code } });
      await qc.invalidateQueries({ queryKey: ['me'] });
      void nav(params.get('next') || '/', { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <PageHead title="Anmelden" />
      {step === 'email' ? (
        <form key="email" className="form panel" onSubmit={ev => void requestCode(ev)} noValidate>
          <p className="note">
            Gib die E-Mail-Adresse ein, unter der du die Sportplaner-Nachrichten bekommst. Wir schicken dir einen Code –
            ein Passwort brauchst du nicht. <Link to="/hilfe#anmelden">Hilfe</Link>
          </p>
          <label className="fl">
            <span>E-Mail-Adresse</span>
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
          </label>
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !email}>
              {busy ? 'Sende …' : 'Code senden'}
            </button>
          </div>
        </form>
      ) : (
        <form key="code" className="form panel" onSubmit={ev => void verify(ev)} noValidate>
          <p className="note">
            Wenn <b>{email}</b> im Sportplaner registriert ist, bekommst du gleich eine E-Mail mit einem 6-stelligen
            Code und einem Anmelde-Link (15 Minuten gültig). Tippe in der E-Mail auf „Jetzt anmelden“ oder gib den Code
            hier ein. Keine E-Mail? Schau im Spam-Ordner nach oder frag die Abteilungsleitung.
          </p>
          <label className="fl">
            <span>Code</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              style={{ fontSize: 28, letterSpacing: 8, fontFamily: 'ui-monospace, monospace', textAlign: 'center' }}
            />
          </label>
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setStep('email');
                setCode('');
                setError('');
              }}
            >
              Andere Adresse
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => void requestCode()} disabled={busy}>
              Neuen Code
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
              {busy ? 'Prüfe …' : 'Anmelden'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
